import React, { useState } from 'react';
import { toast } from 'sonner';
import { ClipboardList, AlertTriangle, Link2, Send, Copy, Loader2, BellRing } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { errMsg, fmtDate } from './api';

export const MissingDataCard = ({ session, api, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [url, setUrl] = useState(session.completion_url || '');
  const [email, setEmail] = useState(session.form?.identite?.email || session.completion_sent_to || '');
  const manquants = session.manquants || [];
  if (!manquants.length) return null;

  const genLink = async () => {
    setBusy('link');
    try { const r = await api.link(session.id); setUrl(r.url); toast.success('Lien de complétion généré'); }
    catch (e) { toast.error(errMsg(e, 'Erreur génération du lien')); }
    finally { setBusy(null); }
  };
  const sendLink = async () => {
    setBusy('send');
    try { const r = await api.sendLink(session.id, email); setUrl(r.url); toast.success(`Lien envoyé à ${r.sent_to}`); if (r.echecs?.length) toast.error(`Échec pour : ${r.echecs.join(', ')}`); onChanged(); }
    catch (e) { toast.error(errMsg(e, "Erreur d'envoi")); }
    finally { setBusy(null); }
  };
  const relancer = async () => {
    setBusy('relance');
    try { const r = await api.relancer(session.id, email); toast.success(`Relance envoyée à ${r.sent_to}`); if (r.echecs?.length) toast.error(`Échec pour : ${r.echecs.join(', ')}`); onChanged(); }
    catch (e) { toast.error(errMsg(e, 'Erreur de relance')); }
    finally { setBusy(null); }
  };
  const relances = session.completion_relances || [];
  const copy = () => { navigator.clipboard.writeText(url); toast.success('Lien copié'); };

  return (
    <section className="rounded-xl border-2 border-amber-300 bg-amber-50/40 p-5 space-y-3" data-testid="ia-missing-card">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-amber-800">
        <ClipboardList className="h-4 w-4" /> Données manquantes ({manquants.length}) — récapitulatif pour le maître d'ouvrage
      </h4>
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {manquants.map((m) => (
          <li key={m.key} className="flex items-center gap-1.5 text-sm text-amber-800" data-testid={`ia-missing-${m.key}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> {m.label}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={genLink} disabled={!!busy} className="gap-1.5" data-testid="ia-gen-link-btn">
          {busy === 'link' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Générer le lien de complétion
        </Button>
        <Input type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email1@…, email2@… (séparés par une virgule)"
          title="Plusieurs adresses possibles, séparées par une virgule ou un point-virgule" className="h-9 min-w-[260px] flex-1 max-w-[420px] bg-white" data-testid="ia-link-email-input" />
        <Button size="sm" onClick={sendLink} disabled={!!busy} className="bg-orange-500 hover:bg-orange-600 text-white gap-1.5" data-testid="ia-send-link-btn">
          {busy === 'send' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer le lien par email
        </Button>
        {session.completion_sent_to && (
          <Button size="sm" variant="outline" onClick={relancer} disabled={!!busy} className="gap-1.5 border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="ia-relance-btn">
            {busy === 'relance' ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Relancer
          </Button>
        )}
      </div>
      {url && (
        <div className="flex items-center gap-2 rounded-md bg-white border border-slate-200 px-3 py-2">
          <code className="flex-1 text-xs text-slate-700 truncate" data-testid="ia-completion-url">{url}</code>
          <button onClick={copy} aria-label="Copier le lien" data-testid="ia-copy-link-btn"><Copy className="h-4 w-4 text-slate-500" /></button>
        </div>
      )}
      <p className="text-xs text-slate-500">
        Les réponses du maître d'ouvrage mettent automatiquement la demande à jour (et le devis s'il a déjà été émis). Le lien reste actif jusqu'à la signature du devis.
        {session.completion_sent_to && <> Dernier envoi : <strong>{session.completion_sent_to}</strong> le {fmtDate(session.completion_sent_at)}.</>}
        {relances.length > 0 && <span data-testid="ia-relances-info"> {relances.length} relance(s), dernière le {fmtDate(relances[relances.length - 1].at)}.</span>}
        {session.completed_by_client_at && <> Complété par le client le {new Date(session.completed_by_client_at).toLocaleString('fr-FR')}.</>}
      </p>
    </section>
  );
};
